import React, { useState, useEffect } from 'react';
import schedulingService from '../services/schedulingService';
import useModal from '../hooks/useModal';
import useNotification from '../hooks/useNotification';
import Notification from './common/Notification';
import ConfirmDialog from './common/ConfirmDialog';

const LockedTimeManager = () => {
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [lockedPeriods, setLockedPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);

  useEffect(() => {
    loadLockedPeriods();
  }, []);

  const loadLockedPeriods = async () => {
    try {
      setLoading(true);
      const response = await schedulingService.getLockedTimePeriods();
      setLockedPeriods(response.data || []);
    } catch (err) {
      setError('Failed to load locked time periods');
      console.error('Error loading locked periods:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePeriod = (periodId) => {
    setPeriodToDelete(periodId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!periodToDelete) return;

    try {
      await schedulingService.deleteLockedTimePeriod(periodToDelete);
      await loadLockedPeriods();
      showSuccess('Blocked time deleted successfully');
    } catch (err) {
      showError('Failed to delete blocked time period');
      console.error('Error deleting period:', err);
    } finally {
      setShowDeleteConfirm(false);
      setPeriodToDelete(null);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getRecurrenceDescription = (period) => {
    if (!period.is_recurring) return 'One-time';
    
    try {
      const pattern = JSON.parse(period.recurrence_pattern || '{}');
      
      if (pattern.frequency === 'daily') {
        return 'Daily';
      } else if (pattern.frequency === 'weekly') {
        if (pattern.days_of_week && pattern.days_of_week.length > 0) {
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const selectedDays = pattern.days_of_week
            .sort((a, b) => a - b)
            .map(day => dayNames[day])
            .join(', ');
          return `Weekly: ${selectedDays}`;
        } else {
          return 'Weekly';
        }
      } else if (pattern.frequency === 'monthly') {
        return 'Monthly';
      } else {
        return 'Recurring';
      }
    } catch {
      return 'Recurring';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Blocked Time</h2>
          <p className="text-gray-600 mt-1">Manage your unavailable time periods</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
        >
          Block Time
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {lockedPeriods.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-4">⏰</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Blocked Time</h3>
          <p className="text-gray-600 mb-4">You haven't blocked any time periods yet.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="text-yellow-600 hover:text-yellow-700 font-medium"
          >
            Block your first time period →
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {lockedPeriods.map((period) => {
            const startTime = formatDateTime(period.start_time);
            const endTime = formatDateTime(period.end_time);
            const isUpcoming = new Date(period.start_time) > new Date();
            
            return (
              <div key={period.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">
                        {period.is_recurring ? '🔄' : isUpcoming ? '⏰' : '⏹️'}
                      </span>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {period.reason || 'Blocked Time'}
                        </h4>
                        <div className="text-sm text-gray-600">
                          {startTime.date === endTime.date ? (
                            <>
                              {startTime.date} • {startTime.time} - {endTime.time}
                            </>
                          ) : (
                            <>
                              {startTime.date} {startTime.time} - {endTime.date} {endTime.time}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Duration: {period.duration_hours}h</span>
                        <span>Type: {getRecurrenceDescription(period)}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isUpcoming 
                            ? 'bg-gray-100 text-gray-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isUpcoming ? 'Upcoming' : 'Past'}
                        </span>
                      </div>
                      
                      {period.description && (
                        <p className="text-sm text-gray-600 italic">
                          "{period.description}"
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setEditingPeriod(period)}
                      className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePeriod(period.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateForm && (
        <LockedTimePeriodModal
          onClose={() => setShowCreateForm(false)}
          onSuccess={loadLockedPeriods}
        />
      )}

      {editingPeriod && (
        <LockedTimePeriodModal
          period={editingPeriod}
          onClose={() => setEditingPeriod(null)}
          onSuccess={loadLockedPeriods}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPeriodToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Blocked Time"
        message="Are you sure you want to delete this blocked time? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

const LockedTimePeriodModal = ({ period, onClose, onSuccess }) => {
  const isEditing = !!period;
  
  // Parse existing recurrence pattern
  const parseRecurrencePattern = (pattern) => {
    if (!pattern) return { frequency: 'daily', days_of_week: [] };
    try {
      return JSON.parse(pattern);
    } catch {
      return { frequency: 'daily', days_of_week: [] };
    }
  };

  const [formData, setFormData] = useState({
    start_time: '',
    end_time: '',
    reason: '',
    description: '',
    is_recurring: false,
    recurrence_frequency: 'daily',
    recurrence_days: []
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  // Days of the week for selection
  const daysOfWeek = [
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' },
    { value: 0, label: 'Sunday', short: 'Sun' }
  ];

  // Toggle day selection for custom recurrence
  const toggleDay = (dayValue) => {
    const currentDays = formData.recurrence_days;
    const newDays = currentDays.includes(dayValue)
      ? currentDays.filter(d => d !== dayValue)
      : [...currentDays, dayValue].sort();
    
    setFormData({ ...formData, recurrence_days: newDays });
  };

  // Generate recurrence pattern JSON
  const generateRecurrencePattern = () => {
    if (!formData.is_recurring) return null;

    const pattern = {
      frequency: formData.recurrence_frequency
    };

    if (formData.recurrence_frequency === 'custom' && formData.recurrence_days.length > 0) {
      pattern.frequency = 'weekly'; // Custom days are implemented as weekly recurrence
      pattern.days_of_week = formData.recurrence_days;
    } else if (formData.recurrence_frequency === 'weekly') {
      // Default to weekdays for weekly if no days specified
      pattern.days_of_week = formData.recurrence_days.length > 0 ? formData.recurrence_days : [1, 2, 3, 4, 5];
    }

    return JSON.stringify(pattern);
  };

  // Update form data when period changes (for editing)
  useEffect(() => {
    if (period) {
      // Editing existing period - populate with existing values
      const parsedPattern = parseRecurrencePattern(period.recurrence_pattern);
      // Convert UTC times to local time for datetime-local inputs
      const formatForDateTimeLocal = (utcTimeString) => {
        if (!utcTimeString) return '';
        const date = new Date(utcTimeString);
        // Get local time components
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      const startLocalTime = formatForDateTimeLocal(period.start_time);
      const endLocalTime = formatForDateTimeLocal(period.end_time);
      
      setFormData({
        start_time: startLocalTime,
        end_time: endLocalTime,
        reason: period.reason || '',
        description: period.description || '',
        is_recurring: period.is_recurring || false,
        recurrence_frequency: parsedPattern.frequency || 'daily',
        recurrence_days: parsedPattern.days_of_week || []
      });
    } else {
      // Creating new period - set defaults only if form is empty
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(12, 0, 0, 0); // Default to noon tomorrow
      
      const endTime = new Date(tomorrow);
      endTime.setHours(13, 0, 0, 0); // Default 1 hour duration
      
      setFormData({
        start_time: tomorrow.toISOString().slice(0, 16),
        end_time: endTime.toISOString().slice(0, 16),
        reason: '',
        description: '',
        is_recurring: false,
        recurrence_frequency: 'daily',
        recurrence_days: []
      });
    }
  }, [period]); // Only depend on period prop

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate times
    const startTime = new Date(formData.start_time);
    const endTime = new Date(formData.end_time);
    
    if (endTime <= startTime) {
      setError('End time must be after start time');
      setLoading(false);
      return;
    }

    // Validate custom recurrence
    if (formData.is_recurring && formData.recurrence_frequency === 'custom' && formData.recurrence_days.length === 0) {
      setError('Please select at least one day for custom recurrence');
      setLoading(false);
      return;
    }

    try {
      const submitData = {
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        reason: formData.reason || null,
        description: formData.description || null,
        is_recurring: formData.is_recurring,
        recurrence_pattern: generateRecurrencePattern()
      };

      if (isEditing) {
        await schedulingService.updateLockedTimePeriod(period.id, submitData);
      } else {
        await schedulingService.createLockedTimePeriod(submitData);
      }
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} blocked time`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          {isEditing ? 'Edit Blocked Time' : 'Block Time Period'}
        </h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="e.g., Lunch Break, Personal Time, Court Appearance"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              rows="2"
              placeholder="Additional details about this blocked time..."
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_recurring"
              checked={formData.is_recurring}
              onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
              className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
            />
            <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700">
              Recurring event
            </label>
          </div>

          {formData.is_recurring && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Repeat Pattern
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="recurrence_frequency"
                      value="daily"
                      checked={formData.recurrence_frequency === 'daily'}
                      onChange={(e) => setFormData({ ...formData, recurrence_frequency: e.target.value, recurrence_days: [] })}
                      className="mr-3 text-yellow-500 focus:ring-yellow-500"
                    />
                    <div>
                      <div className="font-medium text-sm">Daily</div>
                      <div className="text-xs text-gray-500">Every day</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="recurrence_frequency"
                      value="weekly"
                      checked={formData.recurrence_frequency === 'weekly'}
                      onChange={(e) => setFormData({ ...formData, recurrence_frequency: e.target.value, recurrence_days: [1, 2, 3, 4, 5] })}
                      className="mr-3 text-yellow-500 focus:ring-yellow-500"
                    />
                    <div>
                      <div className="font-medium text-sm">Weekly</div>
                      <div className="text-xs text-gray-500">Same day each week</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="recurrence_frequency"
                      value="monthly"
                      checked={formData.recurrence_frequency === 'monthly'}
                      onChange={(e) => setFormData({ ...formData, recurrence_frequency: e.target.value, recurrence_days: [] })}
                      className="mr-3 text-yellow-500 focus:ring-yellow-500"
                    />
                    <div>
                      <div className="font-medium text-sm">Monthly</div>
                      <div className="text-xs text-gray-500">Same date each month</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="recurrence_frequency"
                      value="custom"
                      checked={formData.recurrence_frequency === 'custom'}
                      onChange={(e) => setFormData({ ...formData, recurrence_frequency: e.target.value, recurrence_days: [] })}
                      className="mr-3 text-yellow-500 focus:ring-yellow-500"
                    />
                    <div>
                      <div className="font-medium text-sm">Custom Days</div>
                      <div className="text-xs text-gray-500">Choose specific days</div>
                    </div>
                  </label>
                </div>
              </div>

              {(formData.recurrence_frequency === 'custom' || formData.recurrence_frequency === 'weekly') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.recurrence_frequency === 'custom' ? 'Select Days' : 'Weekly on:'}
                  </label>
                  <div className="space-y-3">
                    {/* Preset buttons */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, recurrence_days: [1, 2, 3, 4, 5] })}
                        className="px-3 py-1 text-xs bg-gray-100 text-yellow-600 rounded-full hover:bg-blue-200 transition-colors"
                      >
                        Weekdays
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, recurrence_days: [0, 6] })}
                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                      >
                        Weekends
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, recurrence_days: [1, 3, 5] })}
                        className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
                      >
                        Mon/Wed/Fri
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, recurrence_days: [] })}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                    
                    {/* Day selection buttons */}
                    <div className="flex flex-wrap gap-2">
                      {daysOfWeek.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleDay(day.value)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            formData.recurrence_days.includes(day.value)
                              ? 'bg-yellow-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {day.short}
                        </button>
                      ))}
                    </div>
                  </div>
                  {formData.recurrence_frequency === 'custom' && formData.recurrence_days.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Please select at least one day for custom recurrence
                    </p>
                  )}
                </div>
              )}

              {formData.recurrence_frequency === 'weekly' && formData.recurrence_days.length === 0 && (
                <p className="text-xs text-yellow-600">
                  Defaults to weekdays (Mon-Fri) if no days selected
                </p>
              )}
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
            >
              {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Block Time')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LockedTimeManager;
